import { authedFetch } from '../../utils/backendFetch';

export const orgPayoutsEnabled = process.env.REACT_APP_ORG_PAYOUTS_ENABLED === 'true';
export async function payoutRequest(teamId, action, body, signal) {
  const base = process.env.REACT_APP_STRIPE_CONNECT_URL;
  if (!base) throw new Error('Payout service is not configured.');
  const response = await authedFetch(`${base}/stripe/organizations/${encodeURIComponent(teamId)}/payouts${action ? `/${action}` : ''}`, {
    method: action ? 'POST' : 'GET', signal,
    headers: { 'Content-Type': 'application/json' },
    ...(action ? { body: JSON.stringify(body || {}) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const unavailable = response.status === 404 && ['new-account', 'account-onboarding', 'sync-setups'].includes(action);
    const error = new Error(data.error || (unavailable ? 'Bank setup is not available in this environment yet. Please contact support.' : 'Unable to load payout details. Please retry.'));
    error.status = response.status;
    error.code = data.code;
    throw error;
  }
  return data;
}
export const supportedBank = account => account.currency === 'hkd';
export const bankLabel = account => {
  const name = account.bank_name || 'Bank details pending';
  const label = account.label && account.label !== account.bank_name ? `${account.label} · ` : '';
  return `${label}${name}${account.last4 ? ` •••• ${account.last4}` : ''} · ${(account.currency || 'hkd').toUpperCase()}`;
};
export const reasonLabel = (value) => ({
  manual_payout_schedule_required: 'Automatic bank payouts must be turned off for this payout option. Contact support to complete settlement setup.',
  bank_account_missing: 'Bank account is missing. Select an available account.',
  bank_account_failed: 'Stripe reports a problem with this bank account.',
  payouts_disabled: 'Complete the required account information in Stripe.',
  transfers_disabled: 'Stripe transfers are not enabled for this account.',
  missing_order_route: 'This older payment needs a reviewed payout destination.',
  provider_result_unknown: 'Payment result is being checked. Do not submit another payment.',
  account_disabled: 'This payout option has been disabled.',
  currency_mismatch: 'This bank cannot receive the currency of this payment.',
  unsupported_currency: 'Only HKD bank accounts can be used for these bookings.',
}[value] || String(value || '').replace(/[._]/g, ' '));

export function settlementStatus(route) {
  const attempts = [...(route.attempts || [])].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  const payout = attempts.find(a => a.kind === 'payout');
  if (payout?.state === 'paid') return 'Bank paid';
  if (attempts.some(a => ['creating', 'unknown'].includes(a.state))) return 'Checking payment';
  if (payout && ['pending', 'in_transit'].includes(payout.state)) return 'Bank payout in progress';
  if (payout && ['failed', 'canceled'].includes(payout.state)) return 'Bank payout failed';
  if (route.state === 'legacy_transferred') return 'Legacy transfer — reconciliation required';
  if (route.state === 'transferred') return 'Transferred to Stripe';
  if (route.batch?.hold_reason || route.hold_reason || route.state === 'held') return 'Settlement on hold';
  return 'Awaiting settlement';
}
// A UI guard only: the backend locks and validates every reroute again.
export function replacementAccounts(route, accounts) {
  if (route.batch && !route.batch.can_reroute) return [];
  const attempts = route.attempts || [];
  if (route.state === 'legacy_transferred' || attempts.some(a =>
    (a.kind === 'transfer' && ['creating', 'unknown'].includes(a.state)) ||
    (a.kind === 'payout' && !['failed', 'canceled'].includes(a.state)))) return [];
  const transferred = route.state === 'transferred' || attempts.some(a => a.kind === 'transfer' && a.state === 'succeeded');
  if (transferred && !attempts.some(a => a.kind === 'payout' && ['failed', 'canceled'].includes(a.state))) return [];
  return accounts.filter(a => a.active && a.status === 'ready' && a.external_account_id &&
    a.payouts_enabled && a.transfers_enabled && a.payout_schedule === 'manual' && a.currency === route.currency &&
    a.id !== route.account_id && (!transferred || a.stripe_account_id === route.stripe_account_id));
}

// Local preview may use an origin already approved by the Dev backend. Never
// let a build-time preview override redirect production users elsewhere.
export function payoutReturnOrigin() {
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_PAYOUT_RETURN_ORIGIN) {
    const origin = new URL(process.env.REACT_APP_PAYOUT_RETURN_ORIGIN);
    if (origin.protocol !== 'https:' || origin.pathname !== '/' || origin.search || origin.hash || origin.username || origin.password) {
      throw new Error('Invalid payout return origin.');
    }
    return origin.origin;
  }
  return window.location.origin;
}
